import { axiom } from '../../lib/axiom';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TaskNode, TaskStatus } from '../task/TaskNode';
import type { NodeStatus } from '@pistachiorama/shared-types';
import { DetailsPopover } from './DetailsPopover';
import { ContextPopover } from './ContextPopover';
import { TaskChat } from '../chat/TaskChat';
import { useStreams } from '../../contexts/StreamsContext';
import { useExecution } from '../../contexts/ExecutionContext';
import { useGraphViewport } from '../../hooks/useGraphViewport';
import { useEdgeDrawing } from '../../hooks/useEdgeDrawing';
import { StreamGraphControls } from './StreamGraphControls';
import { StreamGraphMinimap, getMinimapViewport } from './StreamGraphMinimap';
import { NodeContextMenu } from './NodeContextMenu';
import { StreamGraphModals, BranchModalState, ConnectModalState, StatusModalState } from './StreamGraphModals';
import { getMockGraphData } from './__mocks__/streamGraphData';

const ACTIVE_STATUSES = ['running', 'attention', 'ready', 'failed', 'waiting'];

interface StreamGraphProps {
  streamId: string;
  streamName: string;
  // Props for external node selection control
  selectedNodeId?: string | null;
  onNodeSelect?: (id: string | null) => void;
}
export function StreamGraph({
  streamId,
  streamName,
  // External control props
  selectedNodeId: externalSelectedNode,
  onNodeSelect: externalOnNodeSelect,
}: StreamGraphProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const graphRef = useRef<HTMLDivElement>(null);

  // UI State
  const [selectedNode, setSelectedNodeInternal] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showContext, setShowContext] = useState(false);
  const [showCreateNode, setShowCreateNode] = useState(false);
  const [newNodeName, setNewNodeName] = useState('');
  const [isCreatingNode, setIsCreatingNode] = useState(false);
  const [viewMode, setViewMode] = useState<'all' | 'active'>('all');
  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; nodeId: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBranching, setIsBranching] = useState(false);
  const [showBranchModal, setShowBranchModal] = useState<BranchModalState | null>(null);
  const [branchName, setBranchName] = useState('');
  const [showStatusModal, setShowStatusModal] = useState<StatusModalState | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState<ConnectModalState | null>(null);
  const [isCreatingEdgeFromModal, setIsCreatingEdgeFromModal] = useState(false);

  // Sync external node selection with internal state
  useEffect(() => {
    if (externalSelectedNode !== undefined) {
      setSelectedNodeInternal(externalSelectedNode);
    }
  }, [externalSelectedNode]);

  // Wrapper to notify external callback
  const setSelectedNode = (nodeId: string | null) => {
    setSelectedNodeInternal(nodeId);
    externalOnNodeSelect?.(nodeId);
  };

  // Get graph data from context (real data) or fall back to mock data
  const { currentStreamData, createNode, updateNode, deleteNode, createEdge, autoLayoutStream } = useStreams();
  const { stopExecution, executingNodeIds } = useExecution();
  // Transform API data to component format, or use mock data as fallback
  const { NODES, EDGES } = useMemo(() => {
    if (currentStreamData && currentStreamData.id === streamId) {
      const nodes = currentStreamData.nodes.map((n) => {
        let meta = null;
        if (n.meta) {
          try {
            meta = typeof n.meta === 'string' ? JSON.parse(n.meta) : n.meta;
          } catch {
            axiom.warn('Failed to parse meta for node', { node_id: n.id, meta: n.meta });
          }
        }
        return {
          id: n.id,
          name: n.name,
          status: n.status as TaskStatus,
          x: n.x,
          y: n.y,
          meta: meta?.description,
          agent: meta?.agent ? { name: meta.agent } : undefined,
        };
      });

      const edges = currentStreamData.edges.map((e) => ({
        from: e.from_node_id,
        to: e.to_node_id,
        label: e.label ?? undefined,
      }));

      return { NODES: nodes, EDGES: edges };
    }

    // Fall back to mock data for streams not yet in database
    const graphData = getMockGraphData(streamId);
    return { NODES: graphData.nodes, EDGES: graphData.edges };
  }, [currentStreamData, streamId]);

  // Filter nodes based on view mode
  const visibleNodes = useMemo(() => {
    if (viewMode === 'all') return NODES;
    return NODES.filter(n => ACTIVE_STATUSES.includes(n.status));
  }, [NODES, viewMode]);

  // Filter edges to only show connections between visible nodes
  const visibleNodeIds = useMemo(() => new Set(visibleNodes.map(n => n.id)), [visibleNodes]);
  const visibleEdges = useMemo(() => {
    return EDGES.filter(e => visibleNodeIds.has(e.from) && visibleNodeIds.has(e.to));
  }, [EDGES, visibleNodeIds]);

  // Count hidden nodes
  const hiddenCount = NODES.length - visibleNodes.length;

  // Get selected node data
  const selectedNodeData = selectedNode ? NODES.find(n => n.id === selectedNode) ?? null : null;

  // Custom hooks for viewport and edge drawing
  const viewport = useGraphViewport(containerRef, NODES);
  const {
    scale,
    panOffset,
    isPanning,
    zoomIn,
    zoomOut,
    fitToWindow,
    panToNode,
    setPanOffset,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  } = viewport;

  const edgeDrawing = useEdgeDrawing(
    graphRef,
    NODES,
    EDGES,
    scale,
    panOffset,
    streamId,
    createEdge
  );
  const {
    dragEdge,
    handleEdgeDragStart,
    handleEdgeDragMove,
    handleEdgeDragEnd,
  } = edgeDrawing;
  // Minimap click handler - pan to clicked location
  const handleMinimapClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    // Minimap constants (must match getMinimapViewport and rendering)
    // const minimapWidth = 200;
    // const minimapHeight = 120;
    const minimapScaleX = 12;
    const minimapScaleY = 8;
    const padding = 16; // p-4 = 16px

    // Convert click position to graph coordinates
    // The nodes in minimap are rendered at: x/12 + 16, y/8 + 16
    // So we reverse: graphX = (clickX - 16) * 12
    const graphX = (clickX - padding) * minimapScaleX;
    const graphY = (clickY - padding) * minimapScaleY;

    // Center the viewport on this graph position
    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;

    setPanOffset({
      x: -graphX + containerWidth / 2 / scale,
      y: -graphY + containerHeight / 2 / scale
    });
  };
  // Minimap node click handler - Pan to node
  const handleMinimapNodeClick = (nodeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const node = NODES.find(n => n.id === nodeId);
    if (!node) return;
    panToNode(node.x, node.y);
    setSelectedNode(nodeId);
  };
  // Calculate SVG path for edges
  const getPath = (start: { x: number; y: number }, end: { x: number; y: number }) => {
    // Node dimensions: 180w x 80h
    const nodeWidth = 180;
    const nodeHeight = 80;

    // Calculate centers
    const startCenterX = start.x + nodeWidth / 2;
    const startCenterY = start.y + nodeHeight / 2;
    const endCenterX = end.x + nodeWidth / 2;
    const endCenterY = end.y + nodeHeight / 2;

    // Determine if nodes are more horizontal or vertical relative to each other
    const dx = endCenterX - startCenterX;
    const dy = endCenterY - startCenterY;

    let startX, startY, endX, endY;

    if (Math.abs(dx) > Math.abs(dy)) {
      // More horizontal - connect right to left
      if (dx > 0) {
        startX = start.x + nodeWidth; // Right edge
        endX = end.x;                  // Left edge
      } else {
        startX = start.x;              // Left edge
        endX = end.x + nodeWidth;      // Right edge
      }
      startY = start.y + nodeHeight / 2;
      endY = end.y + nodeHeight / 2;
    } else {
      // More vertical - connect bottom to top
      if (dy > 0) {
        startY = start.y + nodeHeight; // Bottom edge
        endY = end.y;                   // Top edge
      } else {
        startY = start.y;               // Top edge
        endY = end.y + nodeHeight;      // Bottom edge
      }
      startX = start.x + nodeWidth / 2;
      endX = end.x + nodeWidth / 2;
    }

    return `M ${startX} ${startY} L ${endX} ${endY}`;
  };

  // Calculate SVG canvas size from node positions
  const canvasSize = useMemo(() => {
    const nodeWidth = 180;
    const nodeHeight = 80;
    const padding = 200; // Extra space for edges and arrows

    if (visibleNodes.length === 0) {
      return { width: 2500, height: 1000 };
    }

    const maxX = Math.max(...visibleNodes.map(n => n.x + nodeWidth));
    const maxY = Math.max(...visibleNodes.map(n => n.y + nodeHeight));

    return {
      width: Math.max(2500, maxX + padding),
      height: Math.max(1000, maxY + padding),
    };
  }, [visibleNodes]);

  // Calculate minimap viewport indicator
  const minimapViewport = getMinimapViewport(containerRef, scale, panOffset);

  // Create new node handler
  const handleCreateNode = async () => {
    if (!newNodeName.trim() || !streamId) return;

    setIsCreatingNode(true);
    try {
      // Calculate position for new node (to the right of existing nodes)
      const maxX = NODES.length > 0 ? Math.max(...NODES.map(n => n.x)) : 0;
      const newX = maxX + 250;
      const newY = 150;

      await createNode(streamId, newNodeName.trim(), newX, newY);
      setNewNodeName('');
      setShowCreateNode(false);
    } catch (err) {
      axiom.error('Failed to create node', { error: err instanceof Error ? err.message : String(err) });
    } finally {
      setIsCreatingNode(false);
    }
  };

  // Context menu handler
  const handleNodeContextMenu = (e: React.MouseEvent, nodeId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, nodeId });
  };

  // Close context menu on click outside
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    if (contextMenu) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu]);

  // Delete node handler
  const handleDeleteNode = async (nodeId: string) => {
    setIsDeleting(true);
    setContextMenu(null);
    try {
      await deleteNode(nodeId);
      if (selectedNode === nodeId) {
        setSelectedNode(null);
      }
    } catch (err) {
      axiom.error('Failed to delete node', { error: err instanceof Error ? err.message : String(err) });
    } finally {
      setIsDeleting(false);
    }
  };

  // Branch node handler - creates a new parallel node from the same parent
  const handleBranchNode = (nodeId: string) => {
    const node = NODES.find(n => n.id === nodeId);
    if (node) {
      setContextMenu(null);
      setShowBranchModal({ nodeId, nodeName: node.name });
      setBranchName('');
    }
  };

  // Connect node handler - opens modal to select target node
  const handleConnectNode = (nodeId: string) => {
    const node = NODES.find(n => n.id === nodeId);
    if (node) {
      setContextMenu(null);
      setShowConnectModal({ fromNodeId: nodeId, fromNodeName: node.name });
    }
  };

  // Status change handler - opens modal to select new status
  const handleChangeStatus = (nodeId: string) => {
    const node = NODES.find(n => n.id === nodeId);
    if (node) {
      setContextMenu(null);
      setShowStatusModal({ nodeId, nodeName: node.name, currentStatus: node.status });
    }
  };

  // Update node status
  const handleUpdateStatus = async (newStatus: string) => {
    if (!showStatusModal || !streamId) return;

    setIsUpdatingStatus(true);
    try {
      // Use the updateNode function from context
      await updateNode(showStatusModal.nodeId, { status: newStatus as NodeStatus });
      setShowStatusModal(null);
    } catch (err) {
      axiom.error('Failed to update status', { error: err instanceof Error ? err.message : String(err) });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Create edge from connect modal
  const handleCreateEdgeFromModal = async (toNodeId: string) => {
    if (!showConnectModal || !streamId) return;

    setIsCreatingEdgeFromModal(true);
    try {
      await createEdge(streamId, showConnectModal.fromNodeId, toNodeId);
      setShowConnectModal(null);
    } catch (err) {
      axiom.error('Failed to create edge', { error: err instanceof Error ? err.message : String(err) });
    } finally {
      setIsCreatingEdgeFromModal(false);
    }
  };

  // Create the branch
  const handleCreateBranch = async () => {
    if (!showBranchModal || !branchName.trim() || !streamId) return;

    setIsBranching(true);
    try {
      const sourceNode = NODES.find(n => n.id === showBranchModal.nodeId);
      if (!sourceNode) return;

      // Position new node below the source node
      const newX = sourceNode.x;
      const newY = sourceNode.y + 120;

      // Create the new branch node
      const newNode = await createNode(streamId, branchName.trim(), newX, newY);

      // Find parent edges (edges that point TO the source node)
      const parentEdges = EDGES.filter(e => e.to === showBranchModal.nodeId);

      // Create edges from each parent to the new node
      for (const parentEdge of parentEdges) {
        await createEdge(streamId, parentEdge.from, newNode.id, 'branch');
      }

      setShowBranchModal(null);
      setBranchName('');
    } catch (err) {
      axiom.error('Failed to create branch', { error: err instanceof Error ? err.message : String(err) });
    } finally {
      setIsBranching(false);
    }
  };

  // Pan to node when "View in Graph" is clicked (reserved for future use)
  // const handlePanToNode = (nodeId: string) => {
  //   const node = NODES.find(n => n.id === nodeId);
  //   if (!node) return;
  //   panToNode(node.x, node.y);
  //   setSelectedNode(nodeId);
  // };
  return <div className="flex-1 min-h-0 flex relative overflow-hidden">
      {/* Graph Area */}
      <motion.div ref={containerRef} className={`flex-1 relative bg-[#0B0F17] overflow-hidden select-none ${isPanning ? 'cursor-grabbing' : 'cursor-grab'}`} animate={{
      width: selectedNode ? '55%' : '100%'
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} onMouseDown={handleMouseDown} onMouseMove={(e) => { handleMouseMove(e); handleEdgeDragMove(e); }} onMouseUp={(e) => { handleMouseUp(); handleEdgeDragEnd(e); }} onMouseLeave={(e) => { handleMouseUp(); handleEdgeDragEnd(e); }}>
        {/* Panning Hit Area - This div captures mouse events for panning */}
        <div className="absolute inset-0" />

        {/* Grid Background */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
        backgroundSize: '40px 40px'
      }} />

        {/* View Mode Toggle */}
        <div className="absolute top-4 left-4 flex items-center gap-3 z-20">
          <div className="flex bg-surface-800/90 backdrop-blur border border-surface-600 rounded-lg p-1">
            <button
              onClick={() => setViewMode('all')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'all'
                  ? 'bg-surface-700 text-text-primary'
                  : 'text-text-muted hover:text-text-primary'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setViewMode('active')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'active'
                  ? 'bg-surface-700 text-text-primary'
                  : 'text-text-muted hover:text-text-primary'
              }`}
            >
              Active
            </button>
          </div>
          {viewMode === 'active' && hiddenCount > 0 && (
            <span className="text-xs text-text-muted bg-surface-800/90 backdrop-blur border border-surface-600 px-2 py-1 rounded">
              + {hiddenCount} hidden
            </span>
          )}
        </div>

        {/* Graph Container */}
        <AnimatePresence mode="wait">
          <motion.div key={streamId} ref={graphRef} initial={{
          opacity: 0
        }} animate={{
          opacity: 1,
          scale: scale,
          x: panOffset.x * scale,
          y: panOffset.y * scale
        }} exit={{
          opacity: 0
        }} transition={{
          opacity: { duration: 0.3 },
          scale: { duration: 0 },
          x: { duration: 0 },
          y: { duration: 0 }
        }} className="absolute inset-0 pointer-events-none" style={{
          transformOrigin: '0 0'
        }}>
            <svg className="absolute inset-0" style={{ width: canvasSize.width, height: canvasSize.height }}>
              <defs>
                <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#374151" />
                </marker>
              </defs>
              {visibleEdges.map((edge, i) => {
              const start = visibleNodes.find(n => n.id === edge.from);
              const end = visibleNodes.find(n => n.id === edge.to);
              if (!start || !end) return null;
              return <g key={i}>
                    <path d={getPath(start, end)} fill="none" stroke="#374151" strokeWidth="2" markerEnd="url(#arrowhead)" />
                    {edge.label && <text x={(start.x + end.x) / 2 + 90} y={(start.y + end.y) / 2 + 40} fill="#6B7280" fontSize="12" textAnchor="middle" dy="-5">
                        {edge.label}
                      </text>}
                  </g>;
            })}
              {/* Drag edge preview */}
              {dragEdge && (
                <line
                  x1={dragEdge.fromX}
                  y1={dragEdge.fromY}
                  x2={dragEdge.toX}
                  y2={dragEdge.toY}
                  stroke="#3B82F6"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                  markerEnd="url(#arrowhead)"
                />
              )}
            </svg>

            {visibleNodes.map(node => <div key={node.id} className="absolute task-node pointer-events-auto group" style={{
            left: node.x,
            top: node.y
          }}>
                <TaskNode
                  id={node.id}
                  name={node.name}
                  status={node.status as TaskStatus}
                  agent={node.agent}
                  meta={node.meta}
                  selected={selectedNode === node.id}
                  onClick={() => setSelectedNode(node.id)}
                  onContextMenu={(e) => handleNodeContextMenu(e, node.id)}
                  onStop={executingNodeIds.has(node.id) ? () => stopExecution(node.id) : undefined}
                />
                {/* Connection handle - right side */}
                <div
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-4 h-4 rounded-full bg-primary-500 border-2 border-surface-900 opacity-0 group-hover:opacity-100 cursor-crosshair transition-opacity z-10 hover:scale-125"
                  onMouseDown={(e) => handleEdgeDragStart(node.id, e)}
                  title="Drag to connect"
                />
              </div>)}
          </motion.div>
        </AnimatePresence>

        {/* Controls */}
        <StreamGraphControls
          scale={scale}
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          onFitToWindow={fitToWindow}
          onCreateNode={() => setShowCreateNode(true)}
          onAutoLayout={() => {
            if (containerRef.current) {
              autoLayoutStream(containerRef.current.clientWidth, containerRef.current.clientHeight);
            }
          }}
        />

        {/* Interactive Minimap */}
        <StreamGraphMinimap
          nodes={NODES}
          edges={EDGES}
          selectedNode={selectedNode}
          viewport={minimapViewport}
          onClick={handleMinimapClick}
          onNodeClick={handleMinimapNodeClick}
        />

      </motion.div>

      {/* Task Chat Sidebar - Opens when a node is selected */}
      <AnimatePresence>
        {selectedNode && selectedNodeData && (
          <motion.div
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="absolute top-0 right-0 bottom-0 w-[45%] border-l border-surface-600"
          >
            <TaskChat
              taskId={selectedNodeData.id}
              taskName={selectedNodeData.name}
              taskStatus={selectedNodeData.status}
              streamId={streamId}
              streamName={streamName}
              agentName={selectedNodeData.agent?.name}
              onClose={() => setSelectedNode(null)}
              onShowDetails={() => setShowDetails(true)}
              onShowContext={() => setShowContext(true)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Popovers */}
      {selectedNodeData && (
        <>
          <DetailsPopover isOpen={showDetails} onClose={() => setShowDetails(false)} task={selectedNodeData} streamName={streamName} />
          <ContextPopover isOpen={showContext} onClose={() => setShowContext(false)} task={selectedNodeData} />
        </>
      )}

      {/* Modals */}
      <StreamGraphModals
        // Create Node Modal
        showCreateNode={showCreateNode}
        newNodeName={newNodeName}
        isCreatingNode={isCreatingNode}
        onCreateNodeClose={() => setShowCreateNode(false)}
        onNewNodeNameChange={setNewNodeName}
        onCreateNode={handleCreateNode}
        // Branch Modal
        showBranchModal={showBranchModal}
        branchName={branchName}
        isBranching={isBranching}
        onBranchModalClose={() => setShowBranchModal(null)}
        onBranchNameChange={setBranchName}
        onCreateBranch={handleCreateBranch}
        // Connect Modal
        showConnectModal={showConnectModal}
        nodes={NODES}
        edges={EDGES}
        isCreatingEdge={isCreatingEdgeFromModal}
        onConnectModalClose={() => setShowConnectModal(null)}
        onCreateEdge={handleCreateEdgeFromModal}
        // Status Modal
        showStatusModal={showStatusModal}
        isUpdatingStatus={isUpdatingStatus}
        onStatusModalClose={() => setShowStatusModal(null)}
        onUpdateStatus={handleUpdateStatus}
      />

      {/* Context Menu */}
      <AnimatePresence>
        {contextMenu && (
          <NodeContextMenu
            contextMenu={contextMenu}
            isDeleting={isDeleting}
            onConnect={handleConnectNode}
            onBranch={handleBranchNode}
            onChangeStatus={handleChangeStatus}
            onDelete={handleDeleteNode}
          />
        )}
      </AnimatePresence>
    </div>;
}